// Pure KPI scoring helpers, shared by KPIs.js and ReviewForm.js.
//
// Two bugs motivated extracting this:
//   H7 — inline `k.weight / 100` produced NaN when an assignment's weight was
//        blank/null (a "+ Add KPI" form can post ""), poisoning the whole sum.
//   C2 — ReviewForm computed a score of 0 for an unscored review and then wrote
//        `0 || 3`, fabricating a bogus 3/5. The real fix is to distinguish
//        "not all KPIs scored yet" (finalScore = null → cannot finalise) from a
//        genuine computed score.
//
// `weight` is always coerced with Number(); getScore(item) returns the effective
// 1–5 score for an item, or null/0 when it isn't scored.

/**
 * @param {Array} items         KPI assignment rows (each with a `weight`).
 * @param {(item:any)=>number|null} getScore  effective score for an item, or null.
 * @returns {{weightTotal:number, scoredCount:number, total:number,
 *            allScored:boolean, finalScore:number|null}}
 */
export function kpiScoreSummary(items, getScore) {
  const list = Array.isArray(items) ? items : [];
  let weighted = 0;
  let weightTotal = 0;
  let scoredCount = 0;

  for (const item of list) {
    const weight = Number(item?.weight) || 0; // blank/null/NaN weight → 0, never NaN
    weightTotal += weight;

    const raw = getScore(item);
    const score = Number(raw);
    if (raw != null && raw !== '' && !Number.isNaN(score) && score > 0) {
      weighted += (weight / 100) * score;
      scoredCount += 1;
    }
  }

  const allScored = list.length > 0 && scoredCount === list.length;

  return {
    weightTotal,
    scoredCount,
    total: list.length,
    allScored,
    // Only meaningful once every KPI is scored; null otherwise so callers never
    // fabricate a value for a partially- or un-scored review.
    finalScore: allScored ? Math.round(weighted * 100) / 100 : null,
  };
}

/**
 * Live running weighted total as scores are entered — a preview, so it returns a
 * number (0 when nothing is scored yet) and is NaN-safe on blank weights.
 */
export function liveWeightedTotal(items, getScore) {
  const list = Array.isArray(items) ? items : [];
  return list.reduce((sum, item) => {
    const weight = Number(item?.weight) || 0;
    const score = Number(getScore(item)) || 0;
    return sum + (weight / 100) * score;
  }, 0);
}
